import { onViewportEnter } from "@sazka/utils/viewport-enter";
import { LotteryCountdown } from "./lottery-countdown/lottery-countdown";
import { LotteryCountdownCard } from "./lottery-countdown/lottery-countdown-card";
import { LotteryCountdownLabel } from "./lottery-countdown/lottery-countdown-label";
import { LotteryCountdownOdometer } from "./lottery-countdown/lottery-countdown-odometer";
import { LotteryCountdownServiceResponse } from "./lottery-countdown/lottery-countdown-service-response";
import Odometer from "odometer";
import axios from "axios";
import { FutureDrawDto } from "@sazka/lotteries/js/models/draw-info/future-draw-dto";

type CountdownGame = {
    game: string;
    pattern: string;
};

// FIXME: Check if it is used after PP release
export class LotteryCountdownManager {
    /* eslint-disable no-use-before-define */
    static instance: LotteryCountdownManager;
    countdowns: Array<LotteryCountdown> = [];
    interval: number;
    tickTimeoutHandle: string | number | ReturnType<typeof setTimeout>;

    constructor() {
        $(".timer-wrapper").each((_, elem) => {
            this.countdowns.push(new LotteryCountdownLabel(elem));
        });

        $(".lottery-hero__countdown").each((_, elem) => {
            this.countdowns.push(new LotteryCountdownLabel(elem));
        });

        $(".quick-wagers-slider__timer").each((_, elem) => {
            this.countdowns.push(new LotteryCountdownCard(elem, false));
        });

        $(".calendar-icons.countdown, .lottery-calendar__cards--countdown").each((_, elem) => {
            this.countdowns.push(new LotteryCountdownOdometer(elem));
        });

        $(".lp-hero-redesign__jackpot-countdown .category-timer-wrapper").each((_, elem) => {
            this.countdowns.push(new LotteryCountdownCard(elem, true));
        });

        if (!this.countdowns.length) {
            return;
        }

        this.startOdometer();

        this.initOrResetCountdowns();

        this.useDocumentVisibility();
    }

    initOrResetCountdowns() {
        if (this.tickTimeoutHandle) {
            clearInterval(this.tickTimeoutHandle);
        }

        const uniqueGames: CountdownGame[] = [];
        this.countdowns.forEach((x) => {
            if (uniqueGames.every((game) => game.game !== x.game)) {
                uniqueGames.push({ game: x.game, pattern: x.pattern });
            }
        });

        let promise: Promise<LotteryCountdownServiceResponse | LotteryCountdownServiceResponse[]> = null;

        if (uniqueGames.length === 1 && uniqueGames[0].pattern) {
            promise = this.getFutureDrawTime(uniqueGames[0]);
        } else {
            promise = this.getDrawTimeBulk(uniqueGames);
        }

        promise.then((data) => {
            const handler = (countdownData: LotteryCountdownServiceResponse, index: number) => {
                this.countdowns
                    .filter((x) => x.game === uniqueGames[index].game)
                    .forEach((x) => {
                        x.countdownReset(countdownData.remainingSeconds);
                    });
            };

            if (data instanceof Array) {
                data.forEach(handler);
            } else {
                handler(data, 0);
            }

            this.tickTimeoutHandle = setInterval(() => {
                this.onTick();
            }, 1000);
        });
    }

    onTick() {
        const gamesToLoad: Array<CountdownGame> = [];
        this.countdowns.forEach((x) => {
            if (x.timeRemaining.asSeconds() >= 1) {
                x.timeRemaining = x.timeRemaining.subtract(1000, "milliseconds");
                x.DomRender();
            } else if (gamesToLoad.every((g) => g.game !== x.game)) {
                gamesToLoad.push({ game: x.game, pattern: x.pattern });
            }
        });

        gamesToLoad.forEach((config: CountdownGame) => {
            let promise: Promise<LotteryCountdownServiceResponse> = null;

            if (config.pattern) {
                promise = this.getFutureDrawTime(config);
            } else {
                promise = this.getDrawTime(config.game);
            }

            promise.then((data) => {
                if (data.remainingSeconds <= 0) {
                    return;
                }

                this.countdowns
                    .filter((countdown) => countdown.game === config.game)
                    .forEach((countdown) => countdown.countdownReset(data.remainingSeconds));
            });
        });
    }

    getDrawTimeBulk(games: Array<CountdownGame>) {
        const url = `/api/draw-info/countdown?gameNames=${games.map((x) => x.game).join(",")}`;
        return new Promise((res: (data: Array<LotteryCountdownServiceResponse>) => void, rej) => {
            $.get(url)
                .done((data: Array<LotteryCountdownServiceResponse>) => {
                    res(data);
                })
                .fail((err) => {
                    console.error("Error while requesting countdown time:", err);
                    rej(err);
                });
        });
    }

    getDrawTime(game: string) {
        const url = `/api/draw-info/countdown/${game}`;
        return new Promise((res: (data: LotteryCountdownServiceResponse) => void, rej) => {
            $.get(url)
                .done((data: LotteryCountdownServiceResponse) => {
                    res(data);
                })
                .fail((err) => {
                    console.error("Error while requesting countdown time:", err);
                    rej(err);
                });
        });
    }

    async getFutureDrawTime(config: CountdownGame): Promise<LotteryCountdownServiceResponse> {
        const url = `api/draw-info/future-draws/${config.game}?pattern=${config.pattern}`;

        try {
            const response = await axios.get<FutureDrawDto>(url);
            const remainingSeconds = Math.floor((Date.parse(response.data.drawDate) - new Date().getTime()) / 1000);
            return {
                game: config.game,
                remainingSeconds,
            };
        } catch (err) {
            console.error("Error while requesting countdown time:", err);
        }
    }

    startOdometer() {
        const selector = ".start-odometer";
        const odometerRoots = $(selector);
        if (!$(selector).length) {
            return;
        }

        odometerRoots.each((_, e) => {
            onViewportEnter(e).then(() => {
                const odometer = new Odometer({
                    el: e,
                    value: 0,
                    duration: 1000,
                    format: "( ddd)",
                });
                odometer.render();
            });
        });
    }

    useDocumentVisibility() {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.initOrResetCountdowns();
            }
        });
    }
}
